import { useState } from "react";
import { Save, Loader2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useContent } from "@/contexts/ContentContext";

const SOCIAL_LABELS = {
  x: "X (Twitter)",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
};

const SOCIAL_ORDER = ["x", "instagram", "tiktok", "youtube"];

export const FooterEditor = () => {
  const { content, update } = useContent();
  const [draft, setDraft] = useState(content.footer);
  const [busy, setBusy] = useState(false);

  const onField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const upsertSocial = (id, patch) => {
    const socials = [...(draft.socials || [])];
    const idx = socials.findIndex((s) => s.id === id);
    if (idx >= 0) socials[idx] = { ...socials[idx], ...patch };
    else socials.push({ id, url: "#", visible: false, ...patch });
    setDraft({ ...draft, socials });
  };
  const getSocial = (id) =>
    (draft.socials || []).find((s) => s.id === id) || { id, url: "", visible: false };

  const save = async () => {
    setBusy(true);
    try {
      await update("footer", draft);
      toast.success("Footer updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const visibleCount = (draft.socials || []).filter((s) => s.visible).length;

  return (
    <div className="space-y-5" data-testid="footer-editor">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label>Contact email</Label>
          <Input type="email" data-testid="footer-editor-email" value={draft.email || ""} onChange={onField("email")} />
        </div>
        <div className="space-y-1.5">
          <Label>Copyright line</Label>
          <Input data-testid="footer-editor-copyright" value={draft.copyright_text || ""} onChange={onField("copyright_text")} />
        </div>
        <div className="space-y-1.5">
          <Label>ABN (optional)</Label>
          <Input
            data-testid="footer-editor-abn"
            placeholder="e.g. 12 345 678 901"
            value={draft.abn || ""}
            onChange={onField("abn")}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Tagline (left column)</Label>
        <Textarea rows={2} data-testid="footer-editor-tagline" value={draft.tagline || ""} onChange={onField("tagline")} />
      </div>
      <div className="space-y-1.5">
        <Label>Follow blurb (right column under socials)</Label>
        <Input data-testid="footer-editor-follow" value={draft.follow_blurb || ""} onChange={onField("follow_blurb")} />
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Label className="text-base">Social links</Label>
            <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
              URLs are stored in the backend. Toggle a channel on to show its icon on the public site.
            </p>
          </div>
          <span
            data-testid="footer-editor-social-visible-count"
            className="pf-mono text-[10px] uppercase tracking-wider rounded-full bg-[#FFEEE6] text-[#D94A14] px-2.5 py-1"
          >
            {visibleCount} of {SOCIAL_ORDER.length} visible
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {SOCIAL_ORDER.map((id) => {
            const s = getSocial(id);
            const isOn = !!s.visible;
            return (
              <div
                key={id}
                data-testid={`footer-editor-social-row-${id}`}
                className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3"
              >
                <div className="sm:col-span-3 flex items-center gap-2">
                  {isOn ? (
                    <Eye className="h-4 w-4 text-[#1F9D6A]" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  )}
                  <span className="text-sm font-semibold">{SOCIAL_LABELS[id]}</span>
                </div>
                <div className="sm:col-span-7">
                  <Input
                    data-testid={`footer-editor-social-${id}`}
                    placeholder="https://..."
                    value={s.url || ""}
                    onChange={(e) => upsertSocial(id, { url: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 flex items-center justify-end gap-2">
                  <Label
                    htmlFor={`footer-social-visible-${id}`}
                    className="text-xs text-[hsl(var(--muted-foreground))]"
                  >
                    {isOn ? "On" : "Off"}
                  </Label>
                  <Switch
                    id={`footer-social-visible-${id}`}
                    data-testid={`footer-editor-social-${id}-visible`}
                    checked={isOn}
                    onCheckedChange={(v) => upsertSocial(id, { visible: v })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Label className="text-base">Privacy policy</Label>
            <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
              Shown on the dedicated /privacy page (App Store compliant URL).
            </p>
          </div>
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-editor-view-privacy"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] bg-white px-3 py-1.5 text-xs hover:bg-[hsl(var(--accent))]"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open /privacy
          </Link>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">“Last updated” label (free text)</Label>
          <Input
            data-testid="footer-editor-privacy-updated"
            placeholder="e.g. May 2026"
            value={draft.privacy_last_updated || ""}
            onChange={onField("privacy_last_updated")}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Privacy policy text</Label>
          <Textarea
            rows={10}
            data-testid="footer-editor-privacy"
            value={draft.privacy_policy || ""}
            onChange={onField("privacy_policy")}
            className="font-mono text-sm"
          />
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Tip: separate paragraphs with a blank line.
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="footer-editor-save" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Footer
        </Button>
      </div>
    </div>
  );
};

export default FooterEditor;
